import { useState, useEffect, useRef, type MouseEvent } from "react"

const STORAGE_KEY = "notification-center-width"
const DEFAULT_WIDTH = 384
const MIN_WIDTH = 320
// The dock is a side panel, not a workbench — never let it eat more
// than half the viewport.
const MAX_WIDTH_RATIO = 0.5

function clampToViewport(width: number) {
  return Math.min(width, window.innerWidth * MAX_WIDTH_RATIO)
}

function getStoredWidth() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = Number(stored)
      if (!Number.isNaN(parsed) && parsed >= MIN_WIDTH) {
        return clampToViewport(parsed)
      }
    }
  } catch {
    // ignore
  }
  return clampToViewport(DEFAULT_WIDTH)
}

/**
 * Drag-resize for the notification center dock — the left-anchored twin
 * of useViewSheetResize (the dock grows rightward, the sheet leftward).
 * Width persists so the dock reopens the size the operator left it;
 * double-clicking the handle resets to the default.
 */
export function useNotificationDockResize() {
  const [width, setWidth] = useState(getStoredWidth)
  const widthRef = useRef(width)

  useEffect(() => {
    widthRef.current = width
  }, [width])

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(Math.round(width)))
    } catch {
      // ignore
    }
  }, [width])

  useEffect(() => {
    return () => {
      // Shares the global col-resize cursor class with the view sheet.
      document.documentElement.classList.remove("view-sheet-resizing")
    }
  }, [])

  function onResizeStart(event: MouseEvent) {
    event.preventDefault()
    const startX = event.clientX
    const startWidth = widthRef.current

    document.documentElement.classList.add("view-sheet-resizing")

    function handleMouseMove(e: globalThis.MouseEvent) {
      const newWidth = startWidth + (e.clientX - startX)
      const maxWidth = window.innerWidth * MAX_WIDTH_RATIO
      setWidth(Math.max(MIN_WIDTH, Math.min(maxWidth, newWidth)))
    }

    function handleMouseUp() {
      document.documentElement.classList.remove("view-sheet-resizing")
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)
    }

    document.addEventListener("mousemove", handleMouseMove)
    document.addEventListener("mouseup", handleMouseUp)
  }

  function onResetWidth() {
    setWidth(clampToViewport(DEFAULT_WIDTH))
  }

  return { width, onResizeStart, onResetWidth }
}
